import { Element as SvgElement } from '@svgdotjs/svg.js';
import { Utils } from 'core/utils';

import { HandleBehavior, Placement, Position } from './handle';

function groupToScreenOf(element: SvgElement): DOMMatrix
{
  return ((<any>element).parent().node as SVGGraphicsElement).getScreenCTM();
}

abstract class GroupBehavior implements HandleBehavior
{
  private screenToGroup: DOMMatrix = new DOMMatrix();
  private startPointer: Position = { x: 0, y: 0 };

  public respondToDragBegin(element: SvgElement, pointer: Position): void
  {
    this.screenToGroup = groupToScreenOf(element).inverse();
    this.startPointer = pointer;
  }

  protected movementTo(pointer: Position): Position
  {
    const m = this.screenToGroup;
    const dx = pointer.x - this.startPointer.x;
    const dy = pointer.y - this.startPointer.y;

    return { x: m.a * dx + m.c * dy, y: m.b * dx + m.d * dy };
  }

  public abstract placementAt(
    element: SvgElement, pointer: Position): Placement;
}

export class MoveBehavior extends GroupBehavior
{
  private start: Position = { x: 0, y: 0 };

  public respondToDragBegin(element: SvgElement, pointer: Position): void
  {
    super.respondToDragBegin(element, pointer);
    this.start = { x: element.x() as number, y: element.y() as number };
  }

  public placementAt(element: SvgElement, pointer: Position): Placement
  {
    const movement = this.movementTo(pointer);

    return { x: this.start.x + movement.x, y: this.start.y + movement.y };
  }
}

export class ScaleBehavior extends GroupBehavior
{
  private startX: number = 0;
  private startWidth: number = 0;

  constructor(private side: 1 | -1)
  {
    super();
  }

  public respondToDragBegin(element: SvgElement, pointer: Position): void
  {
    super.respondToDragBegin(element, pointer);
    this.startX = element.x() as number;
    this.startWidth = element.width() as number;
  }

  public placementAt(element: SvgElement, pointer: Position): Placement
  {
    const growth = this.side * this.movementTo(pointer).x;

    return { x: this.startX - growth, width: this.startWidth + 2 * growth };
  }
}

export class RotationBehavior implements HandleBehavior
{
  private startRotation: number = 0;
  private startPointer: Position = { x: 0, y: 0 };

  private pointerRelativeToCenter(
    element: SvgElement, pointer: Position): Position
  {
    const center = new DOMPoint(element.cx() as number, element.cy() as number)
      .matrixTransform(groupToScreenOf(element));

    return { x: pointer.x - center.x, y: pointer.y - center.y };
  }

  public respondToDragBegin(element: SvgElement, pointer: Position): void
  {
    this.startRotation = <any>(<any>element).parent().transform('rotate');
    this.startPointer = this.pointerRelativeToCenter(element, pointer);
  }

  public placementAt(element: SvgElement, pointer: Position): Placement
  {
    const current = this.pointerRelativeToCenter(element, pointer);

    return {
      rotation: this.startRotation +
          Utils.rotationToOrigin(current.x, current.y) -
          Utils.rotationToOrigin(this.startPointer.x, this.startPointer.y),
    };
  }
}
